import { AnimatePresence, motion, useAnimate } from "motion/react";
import React, { useEffect, useMemo, useRef } from "react";
import { DYNAMIC_ISLAND } from "../../../../config/dynamicIsland.configuration";
import { useReducedMotion } from "../../../../hooks/useReducedMotion.hook";
import { resolveRouteTypeFranceBadgeForLine } from "../../../../tools/routeTypeFranceBadge.tools";
import { formatStopName } from "../../../../tools/stopNameFormatter";
import { VehicleInfoChrome } from "../../../MapLibre/vehicleInfoChrome";
import VehicleIslandDelayBadge from "../../../MapLibre/vehicleInfoChrome/VehicleIslandDelayBadge";
import { useVehicleIslandDelayStatusCompact } from "../../../MapLibre/vehicleInfoChrome/useVehicleIslandDelayStatusCompact.hook";
import {
    formatVehicleIslandCenterSubline,
    resolveEffectiveVehicleDelaySec,
    resolveVehicleChromeColorTokens,
    resolveVehicleDelayBadgePresentation,
    VEHICLE_ISLAND_CENTER_SUBLINE_TEXT_CLASS,
    VEHICLE_ISLAND_HEADSIGN_TEXT_CLASS,
    VEHICLE_ISLAND_ROW_CLASS_NAME,
    VEHICLE_ISLAND_TEXT_COLUMN_CLASS_NAME,
    VEHICLE_ISLAND_TRAILING_CLUSTER_CLASS_NAME,
    vehicleIslandSideControlStyle,
} from "../../../MapLibre/vehicleInfoChrome/vehicleInfoChrome.tools";
import { type PopupContent } from "../../../MapLibre/vehicleSelection/vehicleSelection.types";

const { vehicleSessionSwitch } = DYNAMIC_ISLAND.animations;

/** Shell and header background color morph on bus change. */
const VEHICLE_COLOR_MORPH_TRANSITION = {
    "duration": vehicleSessionSwitch.colorDurationS,
    "ease":     "easeInOut",
} as const;

/** Row content handoff — spring settle on enter, quick ease-out on exit. */
const VEHICLE_CONTENT_ENTER_TRANSITION = vehicleSessionSwitch.contentSpring;

const VEHICLE_CONTENT_EXIT_TRANSITION = {
    "duration": vehicleSessionSwitch.contentExitDurationS,
    "ease":     "easeIn",
} as const;

/** Shared enter/exit targets for the clipped vertical handoff. */
const VEHICLE_CONTENT_HANDOFF = {
    "enter": {
        "opacity": 1,
        "y":       0,
        "scale":   1,
    },
    "exit": {
        "opacity": 0,
        "y":       -vehicleSessionSwitch.contentSlidePx,
        "scale":   vehicleSessionSwitch.contentScaleFrom,
    },
    "initial": {
        "opacity": 0,
        "y":       vehicleSessionSwitch.contentSlidePx,
        "scale":   vehicleSessionSwitch.contentScaleFrom,
    },
} as const;

interface DynamicIslandVehicleMorphChromeProps {

    /** Live vehicle popup content. */
    "content": PopupContent;

    /** Whether dark UI palette is active. */
    "isDarkUi": boolean;

    /** Optional inline styles on the outer shell (width/height). */
    "style"?: React.CSSProperties;

    /** Trailing island control (e.g. follow toggle). */
    "trailingAction"?: React.ReactNode;

    /** Fired when the user activates the main island content. */
    "onIslandActivate"?: () => void;

    /** Accessible label when {@link onIslandActivate} is set. */
    "islandActivateAriaLabel"?: string;

    /** Stable session key for coordinated content handoff on bus change. */
    "tripId": string;
}

/**
 * Renders the center subline under the headsign (route type + original headsign).
 *
 * @param content Vehicle popup content.
 * @returns Center subline slot content, or `null` when every segment is hidden.
 */
function renderIslandCenterSubline( content: PopupContent ): React.ReactNode {
    const centerSublineText = formatVehicleIslandCenterSubline(
        content.route_type_france,
        content.trip_headsign,
        content.trip_headsign_original,
    );

    if ( centerSublineText === null ) {
        return null;
    }

    return (
        <div className={VEHICLE_ISLAND_CENTER_SUBLINE_TEXT_CLASS}>
            {centerSublineText}
        </div>
    );
}

/**
 * Island vehicle chrome with in-place morph on bus change (pill squeeze, shell colors, vertical handoff).
 * Falls back to static {@link VehicleInfoChrome} when motion is disabled.
 *
 * @param props Component props.
 * @returns Morphing or static vehicle island chrome.
 */
const DynamicIslandVehicleMorphChrome: React.FC<DynamicIslandVehicleMorphChromeProps> = ( {
    content,
    isDarkUi,
    style,
    trailingAction,
    onIslandActivate,
    islandActivateAriaLabel,
    tripId,
} ) => {
    const reducedMotion = useReducedMotion();
    const morphEnabled = DYNAMIC_ISLAND.animations.enabled && !reducedMotion;
    const isError = content.stop_name === "Erreur lors de la récupération des données";
    const noTripFound = content.stop_name === "Aucun trajet trouvé";
    const colorTokens = useMemo( () => resolveVehicleChromeColorTokens( content ), [ content ] );
    const franceBadge = resolveRouteTypeFranceBadgeForLine( content.route_type_france, content.label );
    const hasLineBadge = content.label !== "" || franceBadge !== null;
    const formattedHeadsign = content.trip_headsign !== undefined && content.trip_headsign !== ""
        ? formatStopName( content.trip_headsign )
        : null;
    const rowRef = useRef<HTMLDivElement>( null );
    const headsignRef = useRef<HTMLDivElement>( null );
    const lineBadgeRef = useRef<HTMLDivElement>( null );
    const isFirstTripMountRef = useRef<boolean>( true );
    const [ pillShellRef, animatePillShell ] = useAnimate<HTMLDivElement>();
    const delaySec = resolveEffectiveVehicleDelaySec( content );
    const { useCompactStatusLine } = useVehicleIslandDelayStatusCompact( {
        delaySec,
        formattedHeadsign,
        headsignRef,
        rowRef,
    } );
    const delayBadgePresentation = useMemo(
        () => resolveVehicleDelayBadgePresentation( content, isDarkUi, { useCompactStatusLine } ),
        [ content, isDarkUi, useCompactStatusLine ],
    );
    const centerSubline = renderIslandCenterSubline( content );
    const hasTextColumn = formattedHeadsign !== null || centerSubline !== null;
    const hasTrailingCluster = delayBadgePresentation.statusLine !== null || trailingAction !== undefined;

    useEffect( () => {
        if ( isFirstTripMountRef.current ) {
            isFirstTripMountRef.current = false;

            return;
        }

        let cancelled = false;

        const runPillSqueeze = async(): Promise<void> => {
            const shellElement = pillShellRef.current;

            if ( shellElement === null || cancelled ) {
                return;
            }

            await animatePillShell(
                shellElement,
                {
                    "scaleX": vehicleSessionSwitch.pillScaleXFrom,
                    "scaleY": vehicleSessionSwitch.pillScaleYFrom,
                },
                {
                    "duration": vehicleSessionSwitch.pillCompressDurationS,
                    "ease":     "easeIn",
                },
            );

            if ( cancelled || pillShellRef.current === null ) {
                return;
            }

            await animatePillShell(
                pillShellRef.current,
                {
                    "scaleX": 1,
                    "scaleY": 1,
                },
                vehicleSessionSwitch.pillReleaseSpring,
            );
        };

        void runPillSqueeze();

        return () => {
            cancelled = true;
        };
    }, [ animatePillShell, pillShellRef, tripId ] );

    const handleIslandKeyDown = ( event: React.KeyboardEvent<HTMLDivElement> ): void => {
        if ( onIslandActivate === undefined ) {
            return;
        }
        if ( event.key === "Enter" || event.key === " " ) {
            event.preventDefault();
            onIslandActivate();
        }
    };

    if ( !morphEnabled || isError || noTripFound ) {
        return (
            <VehicleInfoChrome
                content={content}
                contentAlign="island"
                isDarkUi={isDarkUi}
                islandActivateAriaLabel={islandActivateAriaLabel}
                onIslandActivate={onIslandActivate}
                style={style}
                trailingAction={trailingAction}
            />
        );
    }

    return (
        <motion.div
            className="w-full"
            ref={pillShellRef}
            style={{
                "height":          style?.height,
                "transformOrigin": "center center",
            }}
        >
            <motion.div
                animate={{
                    "background":  colorTokens.shellBackground,
                    "borderColor": colorTokens.borderColor,
                }}
                className="rounded-full shadow-lg overflow-hidden backdrop-blur-sm border-2 flex flex-col h-full w-full"
                transition={VEHICLE_COLOR_MORPH_TRANSITION}
            >
            <motion.div
                animate={{
                    "backgroundColor": colorTokens.headerBackground,
                    "color":           colorTokens.textColor,
                }}
                className="px-1.5 h-full flex items-center w-full"
                transition={VEHICLE_COLOR_MORPH_TRANSITION}
            >
                <div
                    aria-label={onIslandActivate !== undefined ? islandActivateAriaLabel : undefined}
                    className={`relative h-full overflow-hidden ${ VEHICLE_ISLAND_ROW_CLASS_NAME } ${
                        onIslandActivate !== undefined ? "cursor-pointer" : ""
                    }`}
                    onClick={onIslandActivate}
                    onKeyDown={handleIslandKeyDown}
                    ref={rowRef}
                    role={onIslandActivate !== undefined ? "button" : undefined}
                    tabIndex={onIslandActivate !== undefined ? 0 : undefined}
                >
                    <AnimatePresence
                        initial={false}
                        mode="sync"
                    >
                        <motion.div
                            animate={{
                                ...VEHICLE_CONTENT_HANDOFF.enter,
                                "transition": VEHICLE_CONTENT_ENTER_TRANSITION,
                            }}
                            className={`${ VEHICLE_ISLAND_ROW_CLASS_NAME } absolute inset-0`}
                            exit={{
                                ...VEHICLE_CONTENT_HANDOFF.exit,
                                "transition": VEHICLE_CONTENT_EXIT_TRANSITION,
                            }}
                            initial={VEHICLE_CONTENT_HANDOFF.initial}
                            key={tripId}
                            style={{ "transformOrigin": "center center" }}
                        >
                            {hasLineBadge
                                ? (
                                    <div
                                        className="flex shrink-0 items-center justify-center self-center overflow-hidden rounded-full px-3 py-[1px] text-sm font-bold leading-none"
                                        ref={lineBadgeRef}
                                        style={{
                                            "backgroundColor": franceBadge !== null ? "#ffffff" : colorTokens.badgeBackground,
                                            "color":           colorTokens.textColor,
                                            ...vehicleIslandSideControlStyle,
                                        }}
                                    >
                                        {franceBadge !== null
                                            ? (
                                                <img
                                                    alt={franceBadge.label}
                                                    className="h-[14px] w-auto max-w-full object-contain"
                                                    src={franceBadge.icon}
                                                />
                                            )
                                            : content.label}
                                    </div>
                                )
                                : null}
                            {hasTextColumn
                                ? (
                                    <div className={VEHICLE_ISLAND_TEXT_COLUMN_CLASS_NAME}>
                                        {formattedHeadsign !== null
                                            ? (
                                                <div
                                                    className={VEHICLE_ISLAND_HEADSIGN_TEXT_CLASS}
                                                    ref={headsignRef}
                                                >
                                                    {formattedHeadsign}
                                                </div>
                                            )
                                            : null}
                                        {centerSubline}
                                    </div>
                                )
                                : null}
                            {hasTrailingCluster
                                ? (
                                    <div className={VEHICLE_ISLAND_TRAILING_CLUSTER_CLASS_NAME}>
                                        <VehicleIslandDelayBadge presentation={delayBadgePresentation} />
                                        {trailingAction}
                                    </div>
                                )
                                : hasLineBadge
                                    ? (
                                        <div
                                            aria-hidden="true"
                                            className="invisible shrink-0 self-center pointer-events-none"
                                            style={vehicleIslandSideControlStyle}
                                        />
                                    )
                                    : null}
                        </motion.div>
                    </AnimatePresence>
                </div>
            </motion.div>
            </motion.div>
        </motion.div>
    );
};

export default DynamicIslandVehicleMorphChrome;
